// Wizard Step Indicator Component

import { Check, Circle, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Step {
  number: number;
  title: string;
  description: string;
}

const STEPS: Step[] = [
  { number: 1, title: 'Client Profile', description: 'Basic information' },
  { number: 2, title: 'Payer Selection', description: 'Configure payers & plans' },
  { number: 3, title: 'Epic Integration', description: 'EHR connectivity' },
  { number: 4, title: 'Ruleset Templates', description: 'Authorization rules' },
  { number: 5, title: 'SLA & Follow-Up', description: 'Timing defaults' },
  { number: 6, title: 'Review & Activate', description: 'Go-live confirmation' },
];

interface WizardStepIndicatorProps {
  currentStep: number;
  completedSteps: number[];
  onStepClick?: (step: number) => void;
  canNavigate?: (step: number) => boolean;
}

export function WizardStepIndicator({
  currentStep,
  completedSteps,
  onStepClick,
  canNavigate = () => true,
}: WizardStepIndicatorProps) {
  return (
    <nav aria-label="Progress" className="w-full">
      <ol className="flex items-center justify-between">
        {STEPS.map((step, idx) => {
          const isComplete = completedSteps.includes(step.number);
          const isCurrent = currentStep === step.number;
          const isLocked = !canNavigate(step.number);
          const isClickable = !isLocked && onStepClick;

          return (
            <li key={step.number} className="flex-1 relative">
              {/* Connector line */}
              {idx < STEPS.length - 1 && (
                <div
                  className={cn(
                    'absolute top-5 left-1/2 w-full h-0.5',
                    isComplete ? 'bg-primary' : 'bg-border'
                  )}
                  aria-hidden="true"
                />
              )}

              <button
                type="button"
                onClick={() => isClickable && onStepClick(step.number)}
                disabled={isLocked}
                className={cn(
                  'relative flex flex-col items-center group',
                  isClickable && 'cursor-pointer',
                  isLocked && 'cursor-not-allowed opacity-50'
                )}
              >
                {/* Step circle */}
                <span
                  className={cn(
                    'w-10 h-10 flex items-center justify-center rounded-full border-2 z-10 transition-colors',
                    isComplete && 'bg-primary border-primary text-primary-foreground',
                    isCurrent && !isComplete && 'border-primary bg-background text-primary',
                    !isComplete && !isCurrent && 'border-border bg-background text-muted-foreground'
                  )}
                >
                  {isComplete ? (
                    <Check className="w-5 h-5" />
                  ) : isLocked ? (
                    <Lock className="w-4 h-4" />
                  ) : (
                    <Circle className="w-4 h-4" fill={isCurrent ? 'currentColor' : 'none'} />
                  )}
                </span>

                {/* Step label */}
                <span className="mt-2 text-center">
                  <span
                    className={cn(
                      'block text-sm font-medium',
                      isCurrent ? 'text-primary' : 'text-muted-foreground'
                    )}
                  >
                    {step.title}
                  </span>
                  <span className="block text-xs text-muted-foreground hidden lg:block">
                    {step.description}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
